import {CUSTOM_TIER_DESCRIPTIONS} from '../../../shared/customTierCopy';
import {customerQuoteExclusions} from '../../../shared/customerQuoteExclusions';
import {currentInstallationTiming, INSTALLATION_SCHEDULING} from '../../../shared/fulfillmentTerms';
import {packageFinishSummary} from '../../../shared/packageFinish';
import PackageScopeDetails from "./PackageScopeDetails";
import type { PlanPackage, PlanProposal } from "../../../shared/planPackages";
import { PLAN_EXCLUSIONS } from "../../../shared/planPurchaseReview";
import "./PackagePurchase.css";
const money = (n: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(n);
export function packageFacts(
  pkg: PlanPackage,
  productLine: "basic" | "custom",
) {
  const text = pkg.scope.join(" ");
  if (productLine === "custom") {
    const tier = pkg.id === 'signature' ? 'signature' : pkg.id === 'essential' ? 'essential' : 'reserve';
    return [
      ['Your collection', CUSTOM_TIER_DESCRIPTIONS[tier]],
      ['Finishing', packageFinishSummary(pkg.scope)],
    ];
  }
  return [
    [
      "Access",
      pkg.id === "standard"
        ? "Ladder access"
        : /staircase.*without drawers/i.test(text) ? "Staircase · no stair drawers" : "Ladder access",
    ],
    [
      "Storage & upgrades",
      pkg.id === "standard"
        ? "Storage, staircases and optional add-ons excluded"
        : /under-bed storage drawers/i.test(text) ? "Under-bed drawers" : /guardrail upgrade/i.test(text) ? "Selected guardrail upgrade" : "Itemized upgrades only",
    ],
    ["Finishing", "Gloss White or Custom Color, as selected"],
    [
      "Installation",
      /Professional installation/.test(text)
        ? "Professional installation included"
        : "As specified in the written scope",
    ],
  ];
}
export default function PackageScopeReview({
  proposal,
  selected,
  total,
  deposit,
  credit = 0,
  paid = false,
  approved = false,
  compact = false,
}: {
  proposal: PlanProposal;
  selected: string;
  total: number;
  deposit: number;
  credit?: number;
  paid?: boolean;
  approved?: boolean;
  compact?: boolean;
}) {
  const pkg = proposal.packages.find((item) => item.id === selected);
  if (!pkg) return null;
  const timing = approved ? (proposal.timing ?? []) : currentInstallationTiming(proposal.timing?.length ? proposal.timing : [INSTALLATION_SCHEDULING]);
  if(compact)return <section className="checkout-scope-brief" aria-label="Your selected package scope"><header className="scope-intro"><span>YOUR ORDER SPECIFICATIONS</span><h3>{pkg.label} · included work</h3></header><PackageScopeDetails scope={pkg.scope} organized/>{!!proposal.reviewItems?.length&&<><h4>Quoted separately</h4><ul>{proposal.reviewItems.map((line,i)=><li key={i}>{line}</li>)}</ul></>}<section className="scope-conditions"><h4>Exclusions &amp; scheduling</h4><ul>{customerQuoteExclusions(proposal.exclusions??PLAN_EXCLUSIONS).map(line=><li key={line}>{line}</li>)}</ul><ul>{timing.map(line=><li key={line}>{line}</li>)}</ul></section></section>;
  return (
    <section
      className="package-scope-review"
      aria-label="Your selected package and payment summary"
    >
      <span className="purchase-eyebrow">
        {approved ? "YOUR SIGNED PACKAGE" : "REVIEW BEFORE YOU SIGN"}
      </span>
      <h2>{pkg.label} · your project summary</h2>
      <div className="purchase-totals">
        <div>
          <span>Package total</span>
          <strong>{money(total)}</strong>
        </div>
        <div className="purchase-due">
          <span>{paid ? "Deposit received" : "Deposit due now"}</span>
          <strong>{money(deposit)}</strong>
        </div>
        <div>
          <span>Remaining final balance</span>
          <strong>{money(total - deposit - credit)}</strong>
        </div>
      </div>
      {credit > 0 && (
        <p className="purchase-note">
          Verified consultation credit applied: {money(credit)}. This credit is
          already deducted from your deposit.
        </p>
      )}
      <p className="purchase-note">
        The remaining final balance is due after fabrication and before
        delivery / installation. Any separately approved work
        is outside these amounts.
      </p>
      <details className="purchase-sow" key={selected}>
        <summary>
          <span className="purchase-sow-title"><strong>Scope of work</strong><small>{pkg.label} package · Specifications, services &amp; exclusions</small></span>
          <span className="purchase-sow-toggle" aria-hidden="true">+</span>
        </summary>
        <div className="purchase-sow-content">
          <section className="purchase-sow-section" aria-label="Included work">
            <header><span className="purchase-section-number">01</span><div><h3>Included work</h3><p>Room-by-room quantities, finishes and selected details</p></div></header>
            <PackageScopeDetails scope={pkg.scope} />
          </section>
          <section className="purchase-sow-section" aria-label="Exclusions">
            <header><span className="purchase-section-number">02</span><div><h3>Exclusions</h3><p>Items and services outside this package price</p></div></header>
            <ul>
              {customerQuoteExclusions(proposal.exclusions ?? PLAN_EXCLUSIONS).map(line => <li key={line}>{line}</li>)}
              {pkg.id === "standard" && <li>Staircases, storage and optional add-ons shown in the preview are excluded from Standard.</li>}
            </ul>
          </section>
          <section className="purchase-sow-section" aria-label="Delivery and scheduling">
            <header><span className="purchase-section-number">03</span><div><h3>Delivery &amp; scheduling</h3><p>Jobsite allowance, requested timing and project coordination</p></div></header>
            <p>{proposal.deliveryZip ? `Delivery allowance included for ZIP ${proposal.deliveryZip}.` : "Delivery is included only where listed in the package scope."}</p>
            <ul>{timing.map(line => <li key={line}>{line}</li>)}</ul>
            <p>Final dimensions, access and installation details require designer review. A deposit retains design services and authorizes material purchasing and preparation, which can begin before final drawing approval. It does not by itself confirm an installation date.</p>
          </section>
          {!!proposal.reviewItems?.length && <section className="purchase-sow-section purchase-sow-pending" aria-label="Selections awaiting pricing">
            <header><span className="purchase-section-number">04</span><div><h3>Selections awaiting pricing</h3><p>Excluded from this estimate · separate quote and approval required</p></div></header>
            <ul>{proposal.reviewItems.map((line,index) => <li key={index}>{line}</li>)}</ul>
          </section>}
        </div>
      </details>
      {!!proposal.reviewItems?.length && <p className="purchase-sow-exclusion-note">{proposal.reviewItems.length} selection{proposal.reviewItems.length === 1 ? " is" : "s are"} awaiting pricing and excluded from this estimate. See “Selections awaiting pricing” in the scope of work.</p>}
      <p className="purchase-visual-note">
        The 3D room illustrates your design choices. This package’s written
        scope defines your purchase; mattresses, bedding and décor in the
        preview are not supplied.
      </p>
    </section>
  );
}


